import { NO, UIView } from "uicore-ts"
import { LabeledSearchableDropdown } from "../LabeledSearchableDropdown"
import { SearchableDropdown } from "../SearchableDropdown"
// Assuming RHColor is exported from a file in the same directory or nearby.
// Adjust the import path as necessary.
import { RHColor } from "./RHColor"


// MARK: - Internal Styled Dropdown
// Subclassing SearchableDropdown to inject Bootstrap 5.3 styles using the RHColor palette.

class RHSearchableDropdown<T> extends SearchableDropdown<T> {
    
    constructor(elementID?: string) {
        
        super(elementID);
        
        // Configure the trigger button to look like a Bootstrap Form Control (.form-control)
        (this as RHSearchableDropdown<T>).configuredWithObject({
            style: {
                backgroundColor: RHColor.surface.stringValue,
                padding: "0.375rem 0.75rem",
                fontSize: "1rem",
                lineHeight: "1.5",
                color: RHColor.textPrimary.stringValue,
                minHeight: "38px",
                display: "flex",
                alignItems: "center",
                transition: "border-color .15s ease-in-out,box-shadow .15s ease-in-out",
                borderRadius: "0.375rem",
                appearance: "none"
            }
        })
        
        // this._tableView.newReusableViewForIdentifier = (
        //     identifier: string,
        //     rowIndex: number
        // ) => new SearchableDropdownRow(elementID + identifier + rowIndex).configuredWithObject({
        //     stopsPointerEventPropagation: NO
        // })
        
        
    }
    
    
    override updateContentForNormalState() {
        // Override parent to remove the default bottom-border-only style
        // and apply Bootstrap's full border style using RHColor
        this.style.border = `1px solid ${RHColor.border.stringValue}`
        this.style.boxShadow = "none"
        
        // Ensure text color matches the RHColor textPrimary
        this.titleLabel.textColor = RHColor.textPrimary
    }
    
    
    override updateContentForHighlightedState() {
        // Apply Bootstrap focus ring style using RHColor.borderFocus (Primary Tint)
        // and calculate the alpha for the shadow manually
        const focusColor = RHColor.borderFocus
        this.style.borderColor = focusColor.stringValue
        this.style.boxShadow = `0 0 0 0.25rem ${focusColor.colorWithAlpha(0.25).stringValue}`
        
        // Keep text color consistent
        this.titleLabel.textColor = RHColor.textPrimary
    }
    
    
    override layoutSubviews() {
        
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.bounds //.rectangleWithInset(padding)
        
        this._rightImageView.frame = bounds.rectangleWithInset(padding)
            .rectangleWithWidth(24, 1)
            .rectangleWithHeight(24, 0.5).rectangleByAddingY(-15)
        
        // Access internal views via casting to 'any' to respect 'private' access in the base class
        // while fulfilling the styling requirement.
        const containerView = (this as any)._containerView as UIView
        const searchTextField = (this as any)._searchTextField as any
        
        // 1. Style the Dropdown Menu Container (.dropdown-menu)
        if (containerView) {
            containerView.configuredWithObject({
                style: {
                    backgroundColor: RHColor.surface.stringValue,
                    borderRadius: "0.375rem",
                    border: `1px solid ${RHColor.border.stringValue}`,
                    // Standard Bootstrap Dropdown Shadow
                    boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
                    padding: "0.5rem 0",
                    marginTop: "0.25rem"
                }
            })
        }
        
        // 2. Style the Search Input inside the menu
        if (searchTextField) {
            searchTextField.configuredWithObject({
                style: {
                    border: "none",
                    borderBottom: `1px solid ${RHColor.border.stringValue}`,
                    borderRadius: "0",
                    padding: "0.5rem 1rem",
                    margin: "0"
                }
            })
        }
    }
    
}


// MARK: - RH Labeled Searchable Dropdown
// Extends the provided wrapper to use the Bootstrap-styled dropdown and label

export class RHLabeledSearchableDropdown<T> extends LabeledSearchableDropdown<T> {
    
    override dropdown!: RHSearchableDropdown<T>
    
    constructor(elementId?: string) {
        
        super(elementId)
        
        // 1. Replace the default dropdown with our styled version
        this.dropdown.removeFromSuperview()
        this.dropdown = new RHSearchableDropdown<T>(this.elementID + "Dropdown")
            .addedAsSubviewToView(this)
        
        // Re-apply height configuration
        this.dropdown.expandedContainerViewHeight = 350
        
        // 2. Style the Label to match Bootstrap .form-label using RHColor
        this.label.configuredWithObject({
            style: {
                marginBottom: "0.5rem",
                fontWeight: "500",
                fontSize: "1rem",
                lineHeight: "1.5",
                color: RHColor.textPrimary.stringValue
            }
        })
        
    }
    
    
    override layoutSubviews() {
        // We need to layout manually to account for the new margin on the label
        const padding = this.core.paddingLength
        const bounds = this.bounds //.rectangleWithInset(padding)
        
        // Calculate dimensions
        const labelHeight = 24 // Slightly larger for 1rem line-height
        const dropdownHeight = 51 // As defined in base class
        const labelMargin = 2 // 0.5rem (Bootstrap margin-bottom)
        
        // Layout Label
        this.label.frame = bounds.rectangleWithHeight(labelHeight)
        
        // Layout Dropdown (pushed down by margin)
        this.dropdown.frame = this.label.frame.rectangleForNextRow(labelMargin, dropdownHeight)
        
        // Force internal layout if needed
        if (this.dropdown.hidden === NO) {
            this.dropdown.setNeedsLayout()
        }
        
    }
    
    
    override intrinsicContentHeight(constrainingWidth: number = 0): number {
        
        // Calculate height including the new label margin
        const labelHeight = 24
        const dropdownHeight = 51
        const margin = 8
        
        return labelHeight + margin + dropdownHeight
        
    }
    
}
